import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MsalProvider } from '@azure/msal-react'
import { EventType, AccountInfo } from '@azure/msal-browser'
import App from './App'
import { msalInstance } from './auth/msalInstance'
import { ApertureProvider } from './components/ApertureProvider'
import { GeekModeProvider } from './contexts/GeekModeContext'
import { AccessibilityProvider } from './contexts/AccessibilityContext'
import { WorkflowProvider } from './contexts/WorkflowContext'

// Fonts (bundled locally instead of CDN)
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fortawesome/fontawesome-free/css/all.min.css'

import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

async function initializeMsal() {
  // Initialize MSAL
  await msalInstance.initialize()

  // Handle redirect response - MUST complete before rendering
  const response = await msalInstance.handleRedirectPromise()

  if (response) {
    // User just logged in via redirect
    msalInstance.setActiveAccount(response.account)
  } else {
    // Check for existing accounts
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0])
    }
  }

  // Set active account on future login events
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const account = (event.payload as { account: AccountInfo }).account
      msalInstance.setActiveAccount(account)
    }
  })
}

// Initialize MSAL first, then render
initializeMsal().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ApertureProvider>
          <QueryClientProvider client={queryClient}>
            <GeekModeProvider>
              <WorkflowProvider>
                <AccessibilityProvider>
                  <BrowserRouter>
                    <App />
                  </BrowserRouter>
                </AccessibilityProvider>
              </WorkflowProvider>
            </GeekModeProvider>
          </QueryClientProvider>
        </ApertureProvider>
      </MsalProvider>
    </React.StrictMode>,
  )
}).catch((error) => {
  console.error('MSAL initialization failed:', error)
})
